import {
    useMutation,
    UseMutationOptions,
    useQueryClient,
    QueryKey,
    MutationFunction
} from '@tanstack/react-query'

import { AxiosResponse, AxiosError } from 'axios'

/**
 * Opções de configuração para o hook de Atualização Otimista.
 *
 * @template TVariables - Tipo dos dados enviados na request (payload).
 * @template TQueryData - Tipo dos dados já armazenados no cache da query.
 */
export interface UseOptimisticUpdateOptions<TVariables, TQueryData>
    extends UseMutationOptions<AxiosResponse, AxiosError, TVariables, { previousData: TQueryData | undefined }> {
    /** Função assíncrona que realiza a requisição na API */
    mutationFn: MutationFunction<AxiosResponse, TVariables>
    /** Chave (QueryKey) do React Query vinculada aos dados que a UI irá atualizar */
    queryKey: QueryKey
    /** 
     * Lógica de alteração local imediata.
     * Recebe o cache atual, os dados da request, e deve retornar o cache atualizado.
     */
    updater: (oldData: TQueryData | undefined, newData: TVariables) => TQueryData
    /** Callback opcional extra, disparado logo após a prévia local da mutação */
    onMutateCallback?: (variables: TVariables) => void | Promise<void>
    /** Callback opcional extra, disparado caso a requisição falhe */
    onErrorActionCallback?: (error: AxiosError, variables: TVariables, context: { previousData: TQueryData | undefined } | undefined) => void
    /** Callback opcional extra, disparado caso a requisição tenha sucesso */
    onSuccessActionCallback?: (data: AxiosResponse, variables: TVariables, context: { previousData: TQueryData | undefined } | undefined) => void
}

/**
 * Hook utilitário para Atualizações Otimistas (Optimistic Updates).
 * Usado para dar feedback instantâneo ao usuário enquanto a requisição carrega no fundo.
 * 
 * **Como funciona o fluxo?**
 * 1. Cancela outras queries na mesma chave para evitar conflito de dados pendentes.
 * 2. Faz backup do cache atual.
 * 3. Usa a função `updater` para mudar a UI localmente de forma instantânea.
 * 4. *[Em caso de erro]* Faz rollback automático para o backup salvo.
 * 5. *[Sempre no fim]* Invalida a query para forçar re-fetch e garantir integridade.
 * 
 * @example
 * const { mutate } = useOptimisticUpdate({
 *   queryKey: ['user', userId],
 *   mutationFn: editUserApi,
 *   updater: (oldUser, newData) => ({ ...oldUser, ...newData })
 * });
 */
export const useOptimisticUpdate = <
    TVariables = unknown,
    TQueryData = unknown,
>({
    mutationFn,
    queryKey,
    updater,
    onMutateCallback,
    onErrorActionCallback,
    onSuccessActionCallback,
    ...options
}: UseOptimisticUpdateOptions<TVariables, TQueryData>) => {
    const queryClient = useQueryClient()

    return useMutation<AxiosResponse, AxiosError, TVariables, { previousData: TQueryData | undefined }>({
        mutationFn,
        onMutate: async (newVariables) => {
            await queryClient.cancelQueries({ queryKey })

            const previousData = queryClient.getQueryData<TQueryData>(queryKey)

            queryClient.setQueryData<TQueryData>(queryKey, (old) => {
                return updater(old, newVariables)
            })

            if (onMutateCallback) {
                await onMutateCallback(newVariables)
            }

            return { previousData }
        },
        onError: (err, newVariables, context) => {
            const castedContext = context as unknown as { previousData?: TQueryData }
            const prev = castedContext?.previousData
            if (prev !== undefined) {
                queryClient.setQueryData(queryKey, prev)
            }

            if (onErrorActionCallback) {
                onErrorActionCallback(err, newVariables, context)
            }
        },
        onSuccess: (data, variables, context) => {
            if (onSuccessActionCallback) {
                onSuccessActionCallback(data, variables, context)
            }
        },
        onSettled: (data, error, variables, context) => {
            queryClient.invalidateQueries({ queryKey })

            if (options.onSettled) {
                options.onSettled(data, error, variables, context)
            }
        },
        ...options,
    })
}
